import Header from './components/header';
import Footer from './components/footer';
import Layout from '@src/components/layout/Layout';
import { useCallback, useEffect, useState } from 'react';
import PdfViewer from '@src/components/utilities/PDFViewer';
import { enqueueSnackbar } from 'notistack';
import { useFindScanNextNewLazy } from '@src/adapters/hooks/scans/useFindScanNextNew';
import ScanNewDTO from '@src/adapters/dto/scan/ScanNewDTO';
import { useSaveScanCheckLazy } from '@src/adapters/hooks/scans/useSaveScanCheck';
import { ScanCheckDecisionType } from '@src/constants/ScanCheckDecisionType';
import { useGenerateScansLazy } from '@src/adapters/hooks/scans/useGenerateScans';
import { useAuth } from '@src/auth/hooks/useAuth';
import { Button, Typography } from '@mui/material';
import { Role } from '@src/constants/Roles';

function ScanCheck() {
  const { execute: getNext } = useFindScanNextNewLazy();
  const { execute: save } = useSaveScanCheckLazy();
  const { execute: generateScans } = useGenerateScansLazy();
  const { currentHospitalId } = useAuth();
  const [scan, setScan] = useState<ScanNewDTO>();
  const { hasRole } = useAuth();
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [numberOfPages, setNumberOfPages] = useState<number>();
  const [selectedProcedureId, setSelectedProcedureId] = useState<string | null>(
    null
  );
  const [selectedConsultantId, setSelectedConsultantId] = useState<
    string | null
  >(null);

  const [zoomLevel, setZoomLevel] = useState<number>(1);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [rotation, setRotation] = useState(0);
  const [selectedOption, setSelectedOption] = useState<ScanCheckDecisionType>(
    ScanCheckDecisionType.CardItem
  );

  const findNext = useCallback(() => {
    if (currentHospitalId) {
      getNext({ hospitalId: currentHospitalId }).then((result) => {
        if (result.scan) {
          setScan(result.scan);
          setSelectedConsultantId(
            result.scan.consultantId ? result.scan.consultantId : null
          );
          setSelectedProcedureId(
            result.scan.procedureId ? result.scan.procedureId : null
          );
          setRecordCount(result.count);
        } else {
          setScan(undefined);
          setSelectedConsultantId(null);
          setSelectedProcedureId(null);
          setRecordCount(0);
          setNumberOfPages(undefined);
        }
      });
    }
  }, [currentHospitalId, getNext]);

  useEffect(() => {
    findNext();
  }, [findNext]);

  const handleSaveAndNext = () => {
    if (
      !selectedConsultantId &&
      selectedOption !== ScanCheckDecisionType.DoNotMigrate
    ) {
      enqueueSnackbar(
        'Please select a consultant or mark this item as Do Not Migrate',
        { variant: 'error' }
      );
    } else {
      if (scan) {
        save({
          id: scan.id,
          option: selectedOption,
          consultantId: selectedConsultantId ? selectedConsultantId : '',
          procedureId: selectedProcedureId ? selectedProcedureId : '',
        }).then(() => {
          findNext();
          setSelectedOption(ScanCheckDecisionType.CardItem);
        });
      }
    }
  };

  const handleImportFiles = () => {
    generateScans({
      hospitalId: currentHospitalId,
    }).then(() => {
      findNext();
    });
  };

  const handleZoom = (increase: boolean) => {
    if (increase) {
      setZoomLevel((prev) => prev + 0.5);
    } else {
      setZoomLevel((prev) => (prev - 0.5 < 0.5 ? 0.5 : prev - 0.5));
    }
  };

  return (
    <Layout>
      {scan && (
        <>
          <Header
            consultantId={selectedConsultantId}
            procedureId={selectedProcedureId}
            selectedOptionId={selectedOption}
            consultantSelected={setSelectedConsultantId}
            procedureSelected={setSelectedProcedureId}
            setSelectedOption={(value) => setSelectedOption(value.id)}
            saveAndNext={handleSaveAndNext}
          />
          <PdfViewer
            pdfUrl={scan.fileURL}
            setNumberOfPages={setNumberOfPages}
            pageNumber={pageNumber}
            rotation={rotation}
            zoomLevel={zoomLevel}
          />
          {numberOfPages && (
            <Footer
              recordCount={recordCount}
              numberOfPages={numberOfPages}
              currentPage={pageNumber}
              setPageNumber={setPageNumber}
              zoom={handleZoom}
              updateRotation={setRotation}
              onImportFiles={handleImportFiles}
            />
          )}
        </>
      )}
      {!numberOfPages && hasRole(Role.ScanImporter) && (
        <Button
          variant="contained"
          size="large"
          color="primary"
          onClick={handleImportFiles}>
          Import Files
        </Button>
      )}
    </Layout>
  );
}

export default ScanCheck;
